import { Box, Button, Center, Flex, Loader, Select, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import ConfirmationButton from "../../../shared/ConfirmationButton/ConfirmationButton";
import { EntrySelectItemWidget } from "../../../../generator/LogBar/EntrySelectItem/EntrySelectItem";
import { PendingFilesProvider } from "../../../../generator/LogBar/PendingFilesContext";
import { LogbookRefsProvider } from "../../../../generator/Logbook/LogbookRefsContext";
import { useGetApi, usePostApi } from "../../../../hooks";
import { WidgetContainer } from "./DashboardWidget.styled";
import WidgetLogbook, { type ISection } from "./WidgetLogbook";
import { ICol } from "src/interfaces";
import HistoricalSnapshotButton from "../../HistoricalSnapshot/HistoricalSnapshotButton";
import { setUsers as setUsersRedux } from "../../../../redux/slices/entriesGeneratorSlice";
import { getYachtId } from "../../../../utils";
import { IUser } from "../../../shared/UserList/UserList.types";

interface Props {
  data: { weid: string; widgetName: string };
  yachtid: string;
  lbid: string;
}

interface LockInfo {
  locked: boolean;
  you: boolean;
  changed: number;
}

// Helper function to convert form row to structured backend format
function convertRowToStructuredFormat(row: any[]): any[] {
  return [
    {
      type: "col",
      unit: "time",
      value: (() => {
        const timeValue = row[0];
        if (typeof timeValue === "string" && timeValue.trim() !== "") {
          return { time: timeValue, type: "LT" };
        } else if (typeof timeValue === "object" && timeValue?.time) {
          return {
            time: timeValue.time || "",
            type: timeValue.type || "LT",
          };
        } else {
          return { time: "", type: "LT" };
        }
      })(),
    },
    {
      type: "col",
      unit: "userref",
      value: (() => {
        const userValue = row[1];
        if (typeof userValue === "object" && userValue?.userid) {
          const firstname =
            userValue.firstname ||
            (userValue.full_name ? userValue.full_name.split(" ")[0] : "");
          const surname =
            userValue.surname ||
            (userValue.full_name
              ? userValue.full_name.split(" ")[1] || ""
              : "");

          return {
            userid: userValue.userid || null,
            firstname: firstname,
            surname: surname,
            rolename: userValue.rolename || "",
          };
        } else {
          return {
            userid: null,
            firstname: "",
            surname: "",
            rolename: "",
          };
        }
      })(),
    },
    {
      type: "col",
      unit: "large/text",
      value: typeof row[2] === "string" ? row[2] : "",
    },
  ];
}

// Helper function to convert multiple rows to structured format
function convertRowsToStructuredFormat(rows: any[]): any[] {
  return rows
    .filter((row: any) => Array.isArray(row))
    .map((row: any) => convertRowToStructuredFormat(row));
}

function deepMapTimeToString(entry: any) {
  if (!entry?.sections) return entry;
  const mappedSections = entry.sections.map((sec: any) => {
    if (!sec?.rows) return sec;
    const structuredRows = convertRowsToStructuredFormat(sec.rows);

    return {
      ...sec,
      rows: structuredRows,
    };
  });
  return { ...entry, sections: mappedSections };
}

function clearWidgetSection(section: ISection) {
  if (!section?.rows) return section;
  return section.rows.map((r: ICol[]) =>
    r.map((c: ICol) => {
      switch (c.unit) {
        case "time":
          return { time: "", type: "LT" };
        case "userref":
          return { userid: null, firstname: "", surname: "", rolename: "" };
        case "large/text":
        default:
          return "";
      }
    }),
  );
}

function convertBackendToFormFormat(entry: any) {
  if (!entry?.sections) return entry;
  const mappedSections = entry.sections.map((sec: any) => {
    if (!sec?.rows) return sec;

    const formRows = sec.rows
      .filter((row: any) => Array.isArray(row))
      .map((row: any) => {
        return row.map((col: any) => {
          if (col?.type === "col" && col?.value !== undefined) {
            return col.value;
          }
          return col;
        });
      });

    return {
      ...sec,
      rows: formRows,
    };
  });
  return { ...entry, sections: mappedSections };
}

const newRowWithTime = (time: string) => [
  { type: "col", unit: "time", value: { time, type: "LT" } },
  {
    type: "col",
    unit: "userref",
    value: { userid: null, firstname: "", surname: "", rolename: "" },
  },
  { type: "col", unit: "large/text", value: "" },
];

export default function DashboardWidget({ data, yachtid, lbid }: Props) {
  const methods = useForm({ defaultValues: {} });
  const [selectedEntry, setSelectedEntry] = useState(data.weid);
  const selectRef = useRef<HTMLInputElement>(null);
  const justCreated = useRef(false);
  const [locks, setLocks] = useState<Record<string, LockInfo>>({});
  const lockInfo = locks[selectedEntry] || {
    locked: false,
    you: false,
    changed: 0,
  };
  const [currentTimeStamp, setCurrentTimeStamp] = useState<string>();
  const hasLock = lockInfo.locked && lockInfo.you;
  const lockedByOther = lockInfo.locked && !lockInfo.you;

  const [isEditing, setIsEditing] = useState(false);
  const editingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lockIntervalRef = useRef<number | null>(null);
  const hasLockRef = useRef(false);
  const formResetRef = useRef(false);
  const initialLoadRef = useRef(true);
  const acquiringLockRef = useRef(false);

  const [users, setUsers] = useState<IUser[]>([]);
  const [localEntries, setLocalEntries] = useState<
    Array<{ weid: string; createdDate: string }>
  >([]);
  const [currentEntry, setCurrentEntry] = useState<any>(null);

  const { refetch: fetchUsers } = useGetApi<{ list: IUser[] }>({
    request: "get-users-full",
    body: { params: { yachtid: getYachtId() } },
    fetchOnInit: false,
  });

  useEffect(() => {
    if (users.length === 0) {
      fetchUsers().then(res => {
        if (res?.list) {
          setUsers(res.list);
        }
      });
    }
  }, []);

  const { data: listResp, refetch: refetchList } = useGetApi<{
    list?: Array<{ weid: string; createdDate: string }>;
  }>({
    request: "get-widget-entries",
    body: { params: { yachtid, lbid } },
    fetchOnInit: true,
  });
  const entries = useMemo(() => {
    const fetchedList = listResp?.list ?? [];
    const allEntries = [...localEntries, ...fetchedList];
    const uniqueEntries = allEntries.filter(
      (entry, index, self) =>
        self.findIndex(e => e.weid === entry.weid) === index,
    );
    return uniqueEntries;
  }, [listResp, localEntries]);

  const {
    data: entryResp,
    isLoading: loadingEntry,
    refetch: refetchEntry,
  } = useGetApi<{
    data: { entry?: { sections: any[] }; currrent?: string };
  }>({
    request: "get-widget-entry",
    body: { params: { weid: selectedEntry, yachtid, lbid } },
    fetchOnInit: false,
  });

  useEffect(() => {
    if (selectedEntry && !justCreated.current) {
      // Reset initial load flag when switching entries
      initialLoadRef.current = true;
      void refetchEntry().then(resp => {
        if (resp?.data?.entry) {
          setCurrentEntry(resp.data.entry);
        }
      });
    }
  }, [selectedEntry, refetchEntry]);

  useEffect(() => {
    if (currentEntry) {
      formResetRef.current = true;

      if (justCreated.current && selectedEntry) {
        justCreated.current = false;
        const nowTime = dayjs().format("HH:mm");

        const enrichedEntry = {
          ...currentEntry,
          sections: currentEntry.sections.map((sec: any) => {
            const isTimeSection =
              sec.headers?.[0]?.unit === "time" ||
              (sec.headers?.[0]?.name?.toLowerCase().includes("watchkeeping") ??
                false) ||
              (sec.title?.toLowerCase().includes("watchkeeper") ?? false);

            if (isTimeSection) {
              const rows =
                sec.rows && sec.rows.length === 1 && sec.rows[0] === ""
                  ? [[{ time: nowTime, type: "LT" }, null, ""]]
                  : sec.rows;

              return {
                ...sec,
                rows,
              };
            }
            return sec;
          }),
        };
        methods.reset(convertBackendToFormFormat(enrichedEntry));

        formResetRef.current = false;
        setTimeout(() => {
          initialLoadRef.current = false;
        }, 200);
      } else {
        const processedEntry = {
          ...currentEntry,
          sections: currentEntry.sections.map((sec: any) => {
            // Check if rows is empty or contains only empty strings
            if (
              !sec.rows ||
              sec.rows.length === 0 ||
              (sec.rows.length === 1 && sec.rows[0] === "")
            ) {
              const isTimeSection =
                sec.headers?.[0]?.unit === "time" ||
                (sec.headers?.[0]?.name
                  ?.toLowerCase()
                  .includes("watchkeeping") ??
                  false) ||
                (sec.title?.toLowerCase().includes("watchkeeper") ?? false);

              if (isTimeSection) {
                return {
                  ...sec,
                  rows: [newRowWithTime(dayjs().format("HH:mm"))],
                };
              }
            }
            return sec;
          }),
        };
        methods.reset(convertBackendToFormFormat(processedEntry));

        setTimeout(() => {
          formResetRef.current = false;
          initialLoadRef.current = false;
        }, 200);
      }
    }
  }, [currentEntry, methods, selectedEntry]);

  const { post } = usePostApi();

  // Check lock status
  const checkLock = useCallback(() => {
    if (!selectedEntry || !currentTimeStamp) return;

    void post({
      request: "check-widget-lock",
      body: {
        params: {
          weid: selectedEntry,
          current: currentTimeStamp,
        },
      },
      onSuccess: resp => {
        setLocks(prev => ({
          ...prev,
          [selectedEntry]: {
            locked: resp.locked,
            you: resp.you,
            changed: resp.changed,
          },
        }));

        // If entry was changed by someone else, refetch
        if (resp.changed === 1) {
          void refetchEntry();
        }
      },
    });
  }, [selectedEntry, currentTimeStamp, post, refetchEntry]);

  // Acquire lock
  const acquireLock = useCallback(async () => {
    if (!selectedEntry || !currentTimeStamp) return false;

    let success = false;
    await post({
      request: "get-widget-lock",
      body: { params: { weid: selectedEntry, current: currentTimeStamp } },
      onSuccess: resp => {
        setLocks(prev => ({
          ...prev,
          [selectedEntry]: {
            locked: resp.locked,
            you: resp.you,
            changed: resp.changed,
          },
        }));
        success = resp.locked && resp.you;
      },
    });
    return success;
  }, [selectedEntry, currentTimeStamp, post]);

  // Start checking lock status when entry changes
  useEffect(() => {
    if (!selectedEntry || !currentTimeStamp) return;

    // Check lock status every 5 seconds
    if (lockIntervalRef.current) {
      clearInterval(lockIntervalRef.current);
    }

    // Only check if we don't have a lock
    if (!hasLockRef.current) {
      lockIntervalRef.current = window.setInterval(() => {
        checkLock();
      }, 5000);
    }

    return () => {
      if (lockIntervalRef.current) {
        clearInterval(lockIntervalRef.current);
        lockIntervalRef.current = null;
      }
    };
  }, [selectedEntry, currentTimeStamp, checkLock]);

  // Keep lock alive while we have it
  useEffect(() => {
    if (!hasLockRef.current || !selectedEntry || !isEditing) return;

    const keepAliveId = setInterval(() => {
      void post({
        request: "keep-alive-widget-lock",
        body: { params: { weid: selectedEntry } },
      });
    }, 5000);

    return () => clearInterval(keepAliveId);
  }, [isEditing, selectedEntry, post]);

  // Cleanup on unmount
  useEffect(() => {
    const releaseOnClose = () => {
      if (selectedEntry) {
        navigator.sendBeacon(
          "/api",
          JSON.stringify({
            request: "release-widget-lock",
            params: { weid: selectedEntry },
          }),
        );
      }
    };

    window.addEventListener("beforeunload", releaseOnClose);

    return () => {
      window.removeEventListener("beforeunload", releaseOnClose);
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
      }
      if (lockIntervalRef.current) {
        clearInterval(lockIntervalRef.current);
      }
      if (hasLockRef.current && selectedEntry) {
        void post({
          request: "release-widget-lock",
          body: { params: { weid: selectedEntry } },
        });
      }
    };
  }, [selectedEntry, post]);

  const saveEntry = useCallback(
    (entryData: any, entryId: string, timestamp?: string) => {
      void post({
        request: "save-widget-entry",
        body: {
          params: {
            yachtid,
            weid: entryId,
            entry: deepMapTimeToString(entryData),
            _current: timestamp || currentTimeStamp,
          },
        },
        onSuccess: resp => {
          const newTs =
            resp.currrent ?? resp.data?.currrent ?? resp.data?.current;
          if (newTs) {
            setCurrentTimeStamp(newTs);
          }
        },
      });
    },
    [yachtid, post, currentTimeStamp],
  );

  const setEditing = useCallback(() => {
    setIsEditing(true);
    if (editingTimerRef.current) {
      clearTimeout(editingTimerRef.current);
    }
    editingTimerRef.current = setTimeout(() => {
      setIsEditing(false);
      editingTimerRef.current = null;
    }, 5000);
  }, []);

  useEffect(() => {
    const handleEditingChange = async () => {
      if (isEditing && !hasLockRef.current && !acquiringLockRef.current) {
        // User started editing and doesn't have lock - try to acquire it
        const currentLockInfo = locks[selectedEntry] || {
          locked: false,
          you: false,
          changed: 0,
        };
        if (!currentLockInfo.locked || currentLockInfo.you) {
          acquiringLockRef.current = true;
          const acquired = await acquireLock();
          acquiringLockRef.current = false;

          if (acquired) {
            hasLockRef.current = true;
            // Stop checking interval
            if (lockIntervalRef.current) {
              clearInterval(lockIntervalRef.current);
              lockIntervalRef.current = null;
            }
          } else {
            console.warn("Failed to acquire lock");
            setIsEditing(false);
          }
        } else {
          // Locked by someone else, don't try to acquire
          setIsEditing(false);
        }
      } else if (!isEditing && hasLockRef.current) {
        // User stopped editing and has lock - auto-save and release
        if (selectedEntry) {
          await saveEntry(methods.getValues(), selectedEntry);
        }
        await post({
          request: "release-widget-lock",
          body: { params: { weid: selectedEntry } },
        });
        hasLockRef.current = false;
        setLocks(prev => ({
          ...prev,
          [selectedEntry]: { locked: false, you: false, changed: 0 },
        }));
        // Resume checking by starting the interval again
        if (currentTimeStamp) {
          lockIntervalRef.current = window.setInterval(() => {
            checkLock();
          }, 5000);
        }
      }
    };

    handleEditingChange();
  }, [
    isEditing,
    selectedEntry,
    acquireLock,
    saveEntry,
    methods,
    post,
    checkLock,
    currentTimeStamp,
    locks,
  ]);

  // Watch for form changes and trigger editing timer
  useEffect(() => {
    const subscription = methods.watch(() => {
      // Don't trigger editing during form reset or initial load
      if (!formResetRef.current && !initialLoadRef.current) {
        setEditing();
      }
    });

    return () => {
      subscription.unsubscribe();
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
      }
    };
  }, [methods, setEditing]);

  const deleteWidgetRow = useCallback(
    async (sectionIndex: number, rowIndex: number) => {
      if (!selectedEntry) return;

      // Acquire lock if we don't have it
      if (!hasLockRef.current) {
        const acquired = await acquireLock();
        if (!acquired) {
          alert("Could not acquire lock. Entry may be locked by another user.");
          return;
        }
        hasLockRef.current = true;
      }

      const currentValues = methods.getValues() as any;
      const section = currentValues.sections?.[sectionIndex];
      if (!section?.rows?.[rowIndex]) return;

      const row = section.rows[rowIndex];
      const structuredRow = convertRowToStructuredFormat(row);

      const sectionCopy = {
        ...section,
        rows: [structuredRow],
        deletedRowIndex: rowIndex,
        sectionIndex: sectionIndex,
      };

      try {
        await post({
          request: "entry-delete-row",
          body: {
            params: {
              lbid,
              rid: selectedEntry,
              row: sectionCopy,
            },
          },
          onSuccess: () => {
            const currentRows = (methods as any).getValues(
              `sections.${sectionIndex}.rows`,
            ) as any[];
            const newRows = [...currentRows];
            newRows.splice(rowIndex, 1);
            (methods as any).setValue(`sections.${sectionIndex}.rows`, newRows);

            saveEntry(methods.getValues(), selectedEntry);
          },
        });
      } catch (error: any) {
        console.error("Error deleting widget row:", error);
      }
    },
    [selectedEntry, methods, post, lbid, saveEntry, acquireLock],
  );

  const formatDate = (date: string) => {
    const d = dayjs.utc(date).local();
    const today = dayjs().format("DD MMM YYYY");

    if (d.format("DD MMM YYYY") === today) {
      return "Today " + d.format("HH:mm");
    }
    return d.format("DD MMM YYYY HH:mm");
  };

  const hasEntryForToday = useMemo(() => {
    const today = dayjs().format("DD MMM YYYY");
    return entries.some(entry => {
      const entryDate = dayjs
        .utc(entry.createdDate)
        .local()
        .format("DD MMM YYYY");
      return entryDate === today;
    });
  }, [entries]);

  const handleSave = useCallback(async () => {
    if (!selectedEntry || lockedByOther) return;

    // Save the entry
    await saveEntry(methods.getValues(), selectedEntry, currentTimeStamp);

    // Release lock if we have it
    if (hasLockRef.current) {
      void post({
        request: "release-widget-lock",
        body: { params: { weid: selectedEntry } },
      });

      hasLockRef.current = false;
      setLocks(prev => ({
        ...prev,
        [selectedEntry]: { locked: false, you: false, changed: 0 },
      }));

      setIsEditing(false);
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
        editingTimerRef.current = null;
      }

      // Resume checking
      if (lockIntervalRef.current) {
        clearInterval(lockIntervalRef.current);
      }
      lockIntervalRef.current = window.setInterval(() => {
        checkLock();
      }, 5000);
    }
  }, [
    selectedEntry,
    lockedByOther,
    saveEntry,
    methods,
    currentTimeStamp,
    post,
    checkLock,
  ]);

  const handleNew = useCallback(() => {
    // Release lock if we have it
    if (hasLockRef.current && selectedEntry) {
      void post({
        request: "release-widget-lock",
        body: { params: { weid: selectedEntry } },
      });
      hasLockRef.current = false;
    }

    // Stop editing mode
    setIsEditing(false);
    if (editingTimerRef.current) {
      clearTimeout(editingTimerRef.current);
      editingTimerRef.current = null;
    }

    justCreated.current = true;
    void post({
      request: "new-widget-entry",
      body: { params: { lbid } },
      onSuccess: async (resp: any) => {
        const newWeid = resp.data?.weid;
        const entry = resp.data?.entry;
        if (!newWeid || !entry) {
          justCreated.current = false;
          return;
        }
        setCurrentEntry(entry);
        const newEntry = {
          weid: newWeid,
          createdDate: dayjs.utc().format("YYYY-MM-DD HH:mm:ss"),
        };
        setLocalEntries(prev => [newEntry, ...prev]);
        setSelectedEntry(newWeid);
        setCurrentTimeStamp(dayjs().format("YYYY-MM-DD HH:mm:ss"));
      },
    });
  }, [lbid, post, selectedEntry]);

  const sections: ISection[] = useMemo(() => {
    const raw = currentEntry?.sections ?? [];
    return raw.map((sec: any, idx: number) => ({
      id: sec.id ?? `sec-${idx}`,
      title: sec.title,
      subtitle: sec.subtitle,
      headers: Array.isArray(sec.headers) ? sec.headers : [],
      rows: Array.isArray(sec.rows) ? sec.rows : [],
      dynamicRows: sec.dynamicRows === true || sec.dynamicRows === "true",
    }));
  }, [currentEntry]);

  // Initialize timestamp when entry changes or selection changes
  useEffect(() => {
    if (selectedEntry) {
      const ts =
        entryResp?.data?.currrent || dayjs().format("YYYY-MM-DD HH:mm:ss");
      setCurrentTimeStamp(ts);
    }
  }, [selectedEntry, entryResp]);

  // Release lock when switching entries
  useEffect(() => {
    return () => {
      if (hasLockRef.current) {
        void post({
          request: "release-widget-lock",
          body: { params: { weid: selectedEntry } },
        });
        hasLockRef.current = false;
      }
      // Stop editing mode
      setIsEditing(false);
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
        editingTimerRef.current = null;
      }
    };
  }, [selectedEntry, post]);

  return (
    <FormProvider {...methods}>
      <LogbookRefsProvider>
        <PendingFilesProvider>
          <WidgetContainer>
            <Flex
              align="center"
              justify="space-between"
              wrap="wrap"
              gap={8}
              mb="lg"
              sx={{
                width: "100%",
                paddingLeft: 20,
                paddingRight: 20,
                "@media (max-width: 700px)": {
                  gap: 4,
                  alignItems: "stretch",
                },
              }}>
              <Box
                sx={theme => ({
                  minWidth: 0,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontWeight: 700,
                  fontSize: 20,
                  color: theme.colors.main[6],
                  mr: 12,
                  mb: { base: 0, xs: 0, sm: 0 },
                })}>
                <Text
                  weight={700}
                  size="lg"
                  sx={{
                    textAlign: "center",
                    letterSpacing: 1,
                    fontSize: "20px",
                    color: "var(--mantine-color-main)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  {data.widgetName}
                </Text>
              </Box>

              <Flex
                align="center"
                gap="sm"
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  "@media (max-width: 650px)": {
                    width: "100%",
                    justifyContent: "center",
                  },
                }}>
                <Box
                  sx={{
                    ml: 12,
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  {hasLock && (
                    <IconLock size={24} stroke={2} style={{ color: "green" }} />
                  )}
                  {lockedByOther && (
                    <IconLock size={24} stroke={2} style={{ color: "red" }} />
                  )}
                </Box>
                <Text size="md">Entries</Text>
                <Select
                  ref={selectRef}
                  data={entries.map(e => ({
                    value: e.weid,
                    label: formatDate(e.createdDate),
                  }))}
                  value={selectedEntry}
                  placeholder="Select entry…"
                  size="xs"
                  onChange={v => {
                    if (v) {
                      setSelectedEntry(v);
                      selectRef.current?.blur();
                    }
                  }}
                  itemComponent={EntrySelectItemWidget}
                  style={{ minWidth: 160, width: 175, maxWidth: "100%" }}
                />
                <HistoricalSnapshotButton
                  lbid={lbid}
                  preview
                  previewAvailable={true}
                  icon
                />
                {hasEntryForToday ? (
                  <ConfirmationButton
                    modalText="An entry already exists for today. Are you sure you want to create another entry for the same day?"
                    onConfirm={handleNew}
                    buttonProps={{
                      size: "xs",
                      variant: "outline",
                      style: { minWidth: 60 },
                    }}>
                    New
                  </ConfirmationButton>
                ) : (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={handleNew}
                    style={{ minWidth: 60 }}>
                    New
                  </Button>
                )}
                <Button
                  size="xs"
                  onClick={handleSave}
                  disabled={lockedByOther}
                  style={{ minWidth: 60 }}>
                  Save
                </Button>
              </Flex>
            </Flex>

            <Box
              sx={{
                minHeight: 200,
                maxHeight: 500,
                overflowY: "auto",
                overflowX: "visible",
                width: "100%",
              }}>
              {loadingEntry ? (
                <Center>
                  <Loader />
                </Center>
              ) : sections.length === 0 ? (
                <Center>
                  <Text color="dimmed">No section to render</Text>
                </Center>
              ) : (
                <WidgetLogbook
                  key={selectedEntry}
                  lbid={lbid}
                  sections={sections}
                  users={users}
                  onAutoSave={() => {
                    if (selectedEntry && hasLock) {
                      saveEntry(methods.getValues(), selectedEntry);
                    }
                  }}
                  onDeleteRow={deleteWidgetRow}
                />
              )}
            </Box>
          </WidgetContainer>
        </PendingFilesProvider>
      </LogbookRefsProvider>
    </FormProvider>
  );
}
